/**
 * A reaction the viewer has clicked but the server has not confirmed, and how those
 * clicks paint over the reactions the server last answered with.
 *
 * ## An intent is an END STATE, not a delta
 *
 * `{ "🔥": { reacted: true } }` reads "the viewer wants to be in the 🔥 group", never
 * "add one 🔥". That is what lets a burst collapse — three clicks on one emoji during a
 * single round trip leave ONE intent, and the next write sends the final answer rather
 * than replaying the clicks — and it is what makes the write idempotent, so a retry or a
 * write the server already agrees with costs nothing and changes nothing.
 *
 * Where the intents live, and why they are not in the query cache, is
 * {@link ../_hooks/use-pending-reactions}'s business. This module is the pure half: what
 * an intent is, and what the pills draw once it is applied.
 *
 * ## An intent lives until the RENDERED reactions carry it
 *
 * A write answering is not the end of an intent — `settled` only records that the server
 * agreed. The intent is dropped by {@link reapSettledIntents}, from the reactions a
 * surface has actually rendered.
 *
 * Dropping it at the answer instead leaves a window, one render wide, in which the cache
 * has the reaction but the component still holds the props from before it: a click landing
 * there reads the OLD reactions, with no intent left to correct them, and computes the
 * wrong direction — it re-adds what the viewer meant to take back. Under rapid clicking
 * that window is hit constantly, and the clicks that fall into it do nothing at all. Tying
 * the lifetime to the rendered data closes it by construction: the fallback to the props
 * is only ever taken once the props provably agree.
 *
 * Same shape as `_hooks/use-pending-sends` + `dropLandedPendingSends`, for the same
 * reason — landing, not "the request answered", is what ends an optimistic row's life.
 *
 * ## Identity is a contract
 *
 * {@link applyPendingReactions} hands the SAME array back when every intent already holds.
 * Every bubble subscribes to the intent store, so a fold that rebuilt unconditionally
 * would re-render the reaction cluster of every reacted message in the thread on every
 * click anywhere in it — with no type error and no visible symptom until the thread is
 * long. `toggleViewerReaction` always builds a new array, so the skip has to live here.
 */

import type { ChannelMessageReaction } from "../_api/channel-endpoints";
import { hasViewerReacted, toggleViewerReaction } from "./message-reaction";

/**
 * One emoji's intended end state for the viewer, which click set it, and whether the
 * server has agreed to it yet.
 *
 * `seq` is what lets the write that answers tell "my own intent is still the newest" from
 * "a newer click replaced it while I was in flight" — see `settleReactionIntentIfSeq`. It
 * is unique within one (message, emoji), which is all the comparison needs.
 *
 * `settled` is the permission to forget: only a settled intent may be reaped, and only
 * once the rendered reactions carry it. An unsettled one is still owed a write.
 */
export type ReactionIntent = {
  reacted: boolean;
  seq: number;
  settled: boolean;
};

/** Every emoji the viewer has intended on one message, by reaction type. */
export type ReactionIntents = Record<string, ReactionIntent | undefined>;

/**
 * Shared identity for a message with nothing intended.
 *
 * The store's selector returns this for every message nobody has clicked — which is
 * almost all of them — so it must be one frozen object rather than a fresh `{}`: zustand
 * reads the selector through `useSyncExternalStore`, which re-renders forever on a new
 * reference and warns that `getSnapshot` should be cached. Same trap `use-pending-sends`
 * documents for `NO_PENDING`.
 */
export const NO_REACTION_INTENTS: ReactionIntents = {};

/**
 * Whether the viewer is in this emoji's group once the intents are applied — what the
 * NEXT click on that emoji has to flip.
 *
 * A click handler asks this rather than reading the rendered pill, because two clicks in
 * one frame share one `message` prop: the second can only answer correctly from what the
 * first just intended.
 */
export function isViewerReactionIntended(
  reactions: ChannelMessageReaction[],
  intents: ReactionIntents,
  type: string,
  viewerId: string,
): boolean {
  const intent = intents[type];
  if (intent) return intent.reacted;

  const group = reactions.find((reaction) => reaction.type === type);
  return !!group && hasViewerReacted(group, viewerId);
}

/**
 * The emoji whose write is still owed, with its intent — what the write loop does next.
 *
 * Filtered rather than taken off the front: a hole in the record (a key whose value is
 * missing) must skip that emoji, never end the loop, or every other intent on the message
 * would be abandoned unwritten while still painted.
 */
export function firstUnsettledIntent(
  intents: ReactionIntents,
): [string, ReactionIntent] | undefined {
  return Object.entries(intents).find(
    (entry): entry is [string, ReactionIntent] =>
      !!entry[1] && !entry[1].settled,
  );
}

/**
 * The intents minus the ones these reactions have caught up with — every settled intent
 * whose end state the rendered data now agrees with.
 *
 * Returns the SAME object when nothing landed, so the store can skip notifying and a
 * caller may run it on every render of a bubble.
 *
 * Unsettled intents are never reaped, however the reactions read: the viewer's click is
 * still owed a write, and a cache that happens to agree may simply be a stale copy.
 */
export function reapSettledIntents(
  intents: ReactionIntents,
  reactions: ChannelMessageReaction[],
  viewerId: string,
): ReactionIntents {
  const landed = Object.entries(intents).filter(([type, intent]) => {
    if (!intent?.settled) return false;
    const group = reactions.find((reaction) => reaction.type === type);
    return (!!group && hasViewerReacted(group, viewerId)) === intent.reacted;
  });
  if (landed.length === 0) return intents;

  const remaining = { ...intents };
  for (const [type] of landed) delete remaining[type];
  return remaining;
}

/**
 * The reactions with the viewer forced in or out of each emoji they have intended — what
 * a pill draws, and the whole reason a server answer cannot un-draw a newer click.
 *
 * Reactions by anyone else pass through untouched, so a snapshot that arrived while the
 * click was in flight still brings their reactions with it. The shape is exactly what the
 * server would have answered: `count` is the group's own `users.length`, a group the
 * viewer was alone in disappears, and a brand-new emoji is appended last — so nothing
 * moves when the real answer replaces the guess.
 */
export function applyPendingReactions(
  reactions: ChannelMessageReaction[],
  intents: ReactionIntents,
  viewerId: string,
): ChannelMessageReaction[] {
  return Object.entries(intents).reduce<ChannelMessageReaction[]>(
    (current, [type, intent]) => {
      if (!intent) return current;

      const group = current.find((reaction) => reaction.type === type);
      // An emoji nobody has used reads as "not reacted", so an intent to TAKE one back
      // that is already gone holds here too — without this, `toggleViewerReaction` would
      // read the missing group as "add" and paint a reaction the viewer just removed.
      const reactedNow = !!group && hasViewerReacted(group, viewerId);
      // The same array back when the intent already holds — see the module doc.
      if (reactedNow === intent.reacted) return current;

      return toggleViewerReaction(current, type, { id: viewerId });
    },
    reactions,
  );
}
